/**
Copyright 2016 Split Software

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
**/
import logFactory from '../../utils/logger';
import { SplitError } from '../../utils/lang/Errors';
var log = logFactory('splitio-producer:my-segments');
import mySegmentsFetcher from '../fetcher/MySegments';
export default function MySegmentsUpdaterFactory(context) {
  var _context$getAll = context.getAll(),
      settings = _context$getAll[context.constants.SETTINGS],
      readiness = _context$getAll[context.constants.READINESS],
      storage = _context$getAll[context.constants.STORAGE],
      metricCollectors = _context$getAll[context.constants.COLLECTORS];

  var segmentsEventEmitter = readiness.segments;
  var readyOnAlreadyExistentState = true;
  var startingUp = true; // @TODO if allowing custom storages, handle async execution and wrap errors as SplitErrors to distinguish from user callback errors

  function updateSegments(segments) {
    // Update the list of segment names available
    var shouldNotifyUpdate = storage.segments.resetSegments(segments); // Notify update if required

    if (storage.splits.usesSegments() && (shouldNotifyUpdate || readyOnAlreadyExistentState)) {
      readyOnAlreadyExistentState = false;
      segmentsEventEmitter.emit(segmentsEventEmitter.SDK_SEGMENTS_ARRIVED);
    }
  }
  /**
   * MySegments updater returns a promise that resolves with a `false` boolean value if it fails to fetch mySegments or synchronize them with the storage.
   *
   * @param {number | undefined} retry current number of retry attemps. this param is only set by SplitChangesUpdater itself.
   * @param {string[] | undefined} segmentList list of mySegment names to sync in the storage. If the list is `undefined`, it fetches them before syncing in the storage.
   * @param {boolean | undefined} noCache true to revalidate data to fetch
   */


  return function MySegmentsUpdater() {
    var retry = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
    var segmentList = arguments.length > 1 ? arguments[1] : undefined;
    var noCache = arguments.length > 2 ? arguments[2] : undefined;
    var updaterPromise;

    if (segmentList) {
      // If segmentList is provided, there is no need to fetch mySegments
      updaterPromise = new Promise(function (res) {
        updateSegments(segmentList);
        res();
      });
    } else {
      // NOTE: We only collect metrics on startup.
      updaterPromise = mySegmentsFetcher(settings, startingUp, metricCollectors, noCache).then(function (segments) {
        // Only when we have downloaded segments completely, we should not keep retrying anymore
        startingUp = false;
        updateSegments(segments);
      });
    }

    return updaterPromise.catch(function (error) {
      // handle user callback errors
      if (!(error instanceof SplitError)) setTimeout(function () {
        throw error;
      }, 0);

      if (startingUp && settings.startup.retriesOnFailureBeforeReady > retry) {
        retry += 1;
        log.warn("Retrying download of segments #".concat(retry, ". Reason: ").concat(error));
        return MySegmentsUpdater(retry);
      } else {
        startingUp = false;
      }

      return false; // shouldUpdate = false
    });
  };
}